// Generated by tutorials/record.cjs from tutorials/articles/h63-getting-started.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h63 --text-only` (wording) or
// `node tutorials/record.cjs h63` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'getting-started',
  catalogId: 'h63',
  title: 'Getting Started: setting up a new facility',
  summary: 'If your facility is new to Dietech Online, you may see Getting Started: a list of setup steps that takes you from your first answers to signing off that you are ready for your first service.',
  audience: 'Administrators setting up a facility that is new to Dietech Online',
  area: 'setup',
  learn: [
    'Find Getting Started and see where each step stands',
    'What each of the twelve steps sets up',
    'Why a step is locked or needs a look',
    'Sign off that you are ready to go live',
    'Bring in PSI\'s latest food data with Data Update, and match your allergy groups',
  ],
  keywords: [
    'getting started',
    'setup',
    'set up',
    'new facility',
    'onboarding',
    'setup checklist',
    'setup wizard',
    'intake',
    'questionnaire',
    'facility basics',
    'tray line auto setup',
    'allergy protection',
    'allergies and food safety',
    'go-live',
    'go live',
    'sign off',
    'printing and reports',
    'selective touch setup',
    'data update',
    'dietech data update',
    'psi data',
    'allergy matching',
    'auto match',
    'request update',
  ],
  steps: [
    {
      id: 'checklist',
      part: 'Find Getting Started',
      heading: 'The Setup checklist on your dashboard',
      text: 'While your facility is being set up, your dashboard has a Setup checklist card: how many setup steps are finished, and a link to the next one.\n\nYou can also open Getting Started from Setup in the menu on the left.',
      media: {
        image: {
          src: '/help/getting-started/checklist.webp',
          width: 1280,
          height: 184,
        },
        alt: 'The dashboard\'s Setup checklist card: 6 of 11 setup steps finished, a progress bar, Next: Allergies and food safety, and Check allergy protection.',
      },
    },
    {
      id: 'steps',
      part: 'Find Getting Started',
      heading: 'The list of steps',
      text: 'Select the Next link. Getting Started opens on the first step that is not finished.\n\nThe list on the left has every step, with where it stands: Done (a green check), Not started, Optional, or a short warning when a step needs a look. Select a step to open it. Until Facility basics is finished, the steps after it are locked, because they use your meal names and times.\n\nThe bar at the top counts the steps finished.',
      media: {
        image: {
          src: '/help/getting-started/steps.webp',
          width: 1280,
          height: 778,
        },
        clip: {
          src: '/help/getting-started/steps.mp4',
          width: 1280,
          height: 778,
          durationSec: 2,
        },
        alt: 'Getting Started with the list of steps on the left: Intake questionnaire, Facility basics, Units, rooms and tray line, Diets and textures done, Allergies and food safety with a warning, the rest not started, and 6 of 11 steps finished at the top.',
      },
    },
    {
      id: 'intake',
      part: 'The steps',
      heading: '1. Intake questionnaire',
      text: 'Tell PSI how your kitchen runs: the meals you serve, their times and how many carts go out; your units with their rooms and beds; the diets and textures you use (picked from your lists); the supplements you serve; whether you use Selective Touch, credits or an EHR; and the staff who will sign in.\n\nLater steps start from these answers. Finish this step checks that every question is answered.',
      media: {
        image: {
          src: '/help/getting-started/intake.webp',
          width: 1280,
          height: 1046,
        },
        alt: 'The Intake questionnaire with Meals you serve (breakfast 7:30 AM, lunch 12:00 PM, dinner 5:00 PM, 3 carts each) and Units, rooms and beds (Maple, Birch and Willow).',
      },
    },
    {
      id: 'basics',
      part: 'The steps',
      heading: '2. Facility basics',
      text: 'Name your three meals and set their times, pick your time zone, and choose your diet defaults: the house diet, the NPO diet, the regular texture, and the default DRI group that reports use for nutrient targets.\n\nThese are the same settings as in Facility Settings. Facility basics must be finished before the later steps open.',
      media: {
        image: {
          src: '/help/getting-started/basics.webp',
          width: 1280,
          height: 994,
        },
        alt: 'Facility basics: Breakfast 7:30 AM, Lunch 12:00 PM, Dinner 5:00 PM, Eastern Standard Time with daylight saving, and the diet defaults House diet Regular and Regular texture Regular.',
      },
    },
    {
      id: 'tray-line',
      part: 'The steps',
      heading: '3. Units, rooms and tray line',
      text: 'First add your units and the rooms and beds in each one on Unit Rooms. Then this step sets up your tray line in nine short parts: how trays go out at each meal, the carts, the order of the carts and of the rooms, who is left off, placing every resident, a preview, and Apply. It starts from your intake answers.\n\nThe last auto setup can be undone. You can fine-tune the tray line later on Tray Line Setup. The step finishes by itself once every bed is on the tray line.',
      media: {
        image: {
          src: '/help/getting-started/tray-line.webp',
          width: 1280,
          height: 1026,
        },
        alt: 'Units, rooms and tray line, done: 3 units with 30 rooms, every bed on the tray line, and the tray line setup on step 1 of 9, How trays go out, with Carts to the rooms picked for every meal.',
      },
    },
    {
      id: 'link-steps',
      part: 'The steps',
      heading: 'Steps done on other screens',
      text: 'Some steps send you to the screens where the work is done, then you come back and select Mark done (or Skip this step):\n\n4. Diets and textures: check your diet and texture lists. 6. PSI data: bring in PSI\'s recipes and ingredients with Data Update (see the last part of this article). 7. Meal patterns and supplements. 8. Menu: import your menu cycle and set its start date. 9. Residents: enter your residents, or let your EHR send them.\n\nMenu and Residents finish by themselves once a menu cycle is in use and residents are entered.',
      media: {
        image: {
          src: '/help/getting-started/link-steps.webp',
          width: 1280,
          height: 396,
        },
        alt: 'The PSI data step: what it is for, a link to Data Update, and Skip this step, Next and Mark done at the bottom.',
      },
    },
    {
      id: 'allergies',
      part: 'The steps',
      heading: '5. Allergies and food safety',
      text: 'This step checks that each food allergy really protects residents: that it takes the food off the tray, warns in Selective Touch, and blocks it in self-order. It looks at seven things, from the allergy list to where the protection applies, and marks the ones that need you.\n\nA blocking problem must be fixed first: Mark done stays off until there are none, the step cannot be skipped, and go-live waits for it. Open each heading to see what is wrong and how to fix it, then select Check again.',
      media: {
        image: {
          src: '/help/getting-started/allergies.webp',
          width: 1280,
          height: 1046,
        },
        alt: 'Allergies and food safety: 3 blocking problems, and the checks 1. Allergy list, 2. Protection (2 allergies are not protected), 3. Item coverage, 4. Exceptions, 5. EHR allergies and 6. Where it applies.',
      },
    },
    {
      id: 'printing',
      part: 'The steps',
      heading: '10. Printing and reports',
      text: 'Eight parts: the reports you print every day (pinned as your favorites), how tray tickets look and what prints on them, their order, posting menus, labels, the printer for each report, and scheduled reports.\n\nA sample printout on the right changes as you choose, so you can see the result before anything prints. Save and continue moves to the next part. The step is finished when the tray ticket format and what prints on a ticket are saved, and tickets have a printer.',
      media: {
        image: {
          src: '/help/getting-started/printing.webp',
          width: 1280,
          height: 1046,
        },
        clip: {
          src: '/help/getting-started/printing.mp4',
          width: 1280,
          height: 1046,
          durationSec: 4.2,
        },
        alt: 'Printing and reports on part 2, Tray ticket format, with the paper and font size choices on the left and a sample tray ticket in the Preview on the right.',
      },
    },
    {
      id: 'touch',
      part: 'The steps',
      heading: '11. Selective Touch (optional)',
      text: 'If servers take residents\' orders on tablets, this step walks through it in eight parts: the meals and rules, the screens, a preview as a resident sees it, who orders, registering the tablets, credits, printing, and a test order with a test resident to prove it works.\n\nIf you do not use Selective Touch, select We don\'t use Selective Touch. Nothing else depends on it.',
      media: {
        image: {
          src: '/help/getting-started/touch.webp',
          width: 1280,
          height: 1046,
        },
        alt: 'Selective Touch with its eight parts on the left, four of them done, and part 5, Tablets, open: No tablet has entered its code yet, and Register Tablet.',
      },
    },
    {
      id: 'go-live',
      part: 'The steps',
      heading: '12. Go-live check',
      text: 'A last look before your first service: food allergy protection, residents with diet orders, the menu cycle in use, a print agent online, and an IDDSI level on every texture.\n\nAllergy protection is a rule: go-live waits for it. The others are things to look at. When every step before this one is done or skipped, select Sign off: ready to go live. The Setup checklist then leaves your dashboard.',
      media: {
        image: {
          src: '/help/getting-started/go-live.webp',
          width: 1280,
          height: 734,
        },
        alt: 'The Go-live check: 2 food allergies are not protected (Go-live waits for this), the menu cycle in use, a print agent online, and the sign-off button greyed out with what is still to finish.',
      },
    },
    {
      id: 'data-update',
      part: 'Updating Dietech\'s food data',
      heading: 'Ask for an update',
      text: 'Data Update, under Setup, brings in PSI\'s latest recipes, USDA ingredients, units and reference lists. Recipes and ingredients your facility created are never changed.\n\nSelect Request Update. The update runs in the background, so you can leave the page. Last update shows how it went, step by step.',
      media: {
        image: {
          src: '/help/getting-started/data-update.webp',
          width: 1280,
          height: 842,
        },
        alt: 'The Data Update page: Request Update at the top, and Last update, Completed, with its steps from Download PSI data to Record the data version, each Done.',
      },
    },
    {
      id: 'what-changed',
      part: 'Updating Dietech\'s food data',
      heading: 'See what changed',
      text: 'What changed counts the recipes, ingredients and list entries the update added, updated or removed. Kept for your use is something PSI no longer sends that your facility still uses, so it stays as yours.\n\nBelow the counts, every one is listed by name, with what changed. History, at the bottom, lists earlier updates; select View details to open one.',
      media: {
        image: {
          src: '/help/getting-started/what-changed.webp',
          width: 1280,
          height: 952,
        },
        alt: 'What changed: a summary with Recipes 5 added, 7 updated, 1 kept for your use, USDA ingredients, Units and Reference lists, and the list of recipes by name.',
      },
    },
    {
      id: 'allergy-matching',
      part: 'Updating Dietech\'s food data',
      heading: 'Match your allergy groups',
      text: 'PSI\'s ingredients carry PSI\'s allergens. An update links them only to your allergy groups that are matched to a PSI allergy, and stops and asks when PSI uses an allergy none of your groups is matched to.\n\nUnder Allergy matching, the groups not matched yet are listed. Pick the PSI allergy for each one, or select Auto Match to pair them by name. Check the picks, then select Save. Show matched groups too lists the others as well.',
      media: {
        image: {
          src: '/help/getting-started/allergy-matching.webp',
          width: 1280,
          height: 558,
        },
        clip: {
          src: '/help/getting-started/allergy-matching.mp4',
          width: 1280,
          height: 558,
          durationSec: 2.9,
        },
        alt: 'Allergy matching after Auto Match: SHELLFISH, TREE NUT, SOY (ALL) and STRAWBERRIES each paired with a PSI allergy and marked changed, and Save with 4 changes to save.',
      },
    },
  ],
  tips: [
    'You do not have to finish a step in one go. Save and come back later keeps what you have entered, and the Setup checklist on your dashboard takes you back to the next step.',
    'Refresh status, at the top right of Getting Started, checks every step against your data again, for example after you have added rooms or residents on another screen.',
    'Check allergy protection runs the same allergy check as the Allergies and food safety step, at any time. It is on the Setup checklist card and in Tables, Food Allergies.',
  ],
  notes: [
    'Getting Started is switched on for a facility while it is being set up. If you do not see it, your facility does not use it; ask Dietech support if you think it should.',
    'Getting Started is for administrators who can change the facility\'s settings. If it says you do not have access, ask your administrator.',
    'Data Update needs the Data Update level on your role. A facility can ask for an update at most once every few minutes; PSI may also send one.',
  ],
  related: [
    'first-day',
    'tray-line-setup',
    'allergies-likes-dislikes',
    'printers-and-printing',
    'selective-touch',
    'importing-a-menu',
    'settings-and-preferences',
  ],
  recorded: '2026-11-03',
};

export default article;
